import * as React from 'react';
import * as _ from 'lodash';
import styled, {css} from 'styled-components';
import {transparentize} from 'polished';
import {transition} from 'helpers/css';
import outline from 'helpers/outline';
import {IconChevronArrowTop, IconChevronArrowBottom} from 'icons';

export type Count = number;

export enum InputNumberVariant {
  Default = 'default',
  Accent = 'accent',
}

export interface InputNumberProps extends
  Omit<React.InputHTMLAttributes<HTMLInputElement>,
    | 'type'
    | 'value'
    | 'defaultValue'
    | 'onChange'> {
  variant?: InputNumberVariant;
  defaultValue?: number;
  onChange?(count: number): void;
}

export const Container = styled.div<Pick<InputNumberProps, 'variant'>>`
  grid-template-columns: 1fr max-content;
  display: inline-grid;
  border-radius: 5px;
  
  ${({variant}) => {
    switch (variant) {
      case InputNumberVariant.Default: return css`
        background: ${({theme}) => `
          ${transparentize(.3, theme.variables.backgroundColor)};
        `};
      `;
      case InputNumberVariant.Accent: return css`
        background: ${({theme}) => `
          ${theme.variables.accentBackgroundColor};
        `};
      `;
    }
  }};
`;

export const Input = styled.input`
  height: 34px;
  width: 100%;
  grid-row: span 2;
  box-sizing: border-box;
  padding: 0 8px 0 12px;
  text-align: right;
  ${transition()};
`;

export const Control = styled.button`
  width: 20px;
  padding: 0;
  border-radius: 0 5px 0 0;
  ${transition()};
  justify-content: center;
  align-items: center;
  display: grid;
  color: ${({theme}) => `
    ${transparentize(.8, theme.colors.white)};
  `};
  
  &:hover {
    color: ${({theme}) => `
      ${transparentize(.4, theme.colors.white)};
    `};
    background: ${({theme}) => `
      ${transparentize(.95, theme.colors.white)};
    `};
  }
  
  &:active {
    background: ${({theme}) => `
      ${transparentize(.9, theme.colors.white)};
    `};
  }
  
  &:focus-visible {
    ${outline(0, 1)};
  }
  
  &:last-child {
    border-radius: 0 0 5px 0;
  }
`;

/**
 * The input number component
 * @param {InputNumberProps} props
 * @return {React.ReactElement<InputNumberProps>}
 * @constructor
 */
function InputNumber(
    props: InputNumberProps,
): React.ReactElement<InputNumberProps> {
  const [
    count,
    setCount,
  ] = React.useState<number>(
      Number(props.defaultValue)
    || props.minLength || 0,
  );

  const inputProps = _.omit(props, [
    'defaultValue',
    'onChange',
    'disabled',
  ]);

  /**
   * Override setCount hook
   * @param {number} count
   */
  function _setCount(count: number): void {
    setCount(count);
    props.onChange && props.onChange(count);
  }

  /**
   * Handle click up control
   */
  function handleClickUp(): void {
    _setCount(count + 1);
  }

  /**
   * Handle click down control
   */
  function handleClickDown(): void {
    _setCount(count - 1);
  }

  return (
    <Container variant={props.variant}>
      <Input
        {...inputProps}
        type="number"
        value={count}
        disabled={props.disabled}
      />
      <Control type="button" onClick={handleClickUp}>
        <IconChevronArrowTop />
      </Control>
      <Control type="button" onClick={handleClickDown}>
        <IconChevronArrowBottom />
      </Control>
    </Container>
  );
}

InputNumber.defaultProps = {
  variant: InputNumberVariant.Default,
};

export default InputNumber;
